import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import Header from "../components/shared/Header";
import Footer from "../components/shared/Footer";
import { toast } from "sonner";
import { api } from "../App";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { CreditCard, Shield, Check, Loader2, AlertCircle } from "lucide-react";

const PAYPAL_OPTIONS = { clientId: "test", currency: "GBP" };
const PAYPAL_BUTTON_HEIGHT = 55;

const PaymentPage = () => {
  const { bookingId } = useParams();
  const [booking, setBooking] = useState(null);
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingStripe, setProcessingStripe] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("stripe");

  const fetchBookingDetails = useCallback(async () => {
    try {
      const bookingResponse = await api.get(`/bookings/${bookingId}`);
      setBooking(bookingResponse.data);
      
      const quoteResponse = await api.get(`/quotes/${bookingResponse.data.quote_id}`);
      setQuote(quoteResponse.data);
    } catch {
      toast.error("Failed to load booking details");
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => {
    fetchBookingDetails();
  }, [fetchBookingDetails]);

  const handleStripePayment = async () => {
    setProcessingStripe(true);
    try {
      const response = await api.post(`/payments/stripe/create-session?booking_id=${bookingId}`, {
        origin_url: window.location.origin
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      const message = error?.response?.data?.detail || "Failed to initiate payment";
      toast.error(message);
      setProcessingStripe(false);
    }
  };

  const handlePayPalApprove = async (data) => {
    try {
      await api.post(`/payments/paypal/capture-order?booking_id=${bookingId}&order_id=${data.orderID}`);
      window.location.href = `/payment/success?session_id=${data.orderID}&method=paypal`;
    } catch (error) {
      toast.error("Failed to capture PayPal payment");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <main className="pt-20 flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-12 h-12 animate-spin text-[#FF5A1F]" />
        </main>
        <Footer />
      </div>
    );
  }

  if (!booking || !quote) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <main className="pt-20">
          <div className="container-max px-4 sm:px-6 lg:px-8 py-20 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-['Outfit'] font-bold text-[#0F172A] mb-4">Booking Not Found</h1>
            <p className="text-slate-600 mb-8">The booking you're looking for doesn't exist or has expired.</p>
            <Link to="/instant-quote" className="btn-primary" data-testid="payment-back-btn">
              Get a New Quote
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (booking.deposit_paid) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <main className="pt-20">
          <div className="container-max px-4 sm:px-6 lg:px-8 py-20">
            <div className="max-w-xl mx-auto text-center">
              <div className="w-20 h-20 bg-[#16A34A]/10 mx-auto flex items-center justify-center mb-6">
                <Check className="w-10 h-10 text-[#16A34A]" />
              </div>
              <h1 className="text-3xl font-['Outfit'] font-bold text-[#0F172A] mb-4">
                Payment Already Received
              </h1>
              <p className="text-slate-600 mb-8">
                Your deposit has already been paid. You should have received a confirmation email.
              </p>
              <Link to="/" className="btn-primary" data-testid="payment-home-btn">
                Return Home
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="pt-20">
        <section className="section-padding">
          <div className="container-max">
            <div className="max-w-4xl mx-auto">
              <div className="grid lg:grid-cols-5 gap-12">
                {/* Order Summary */}
                <div className="lg:col-span-2">
                  <div className="bg-[#F8F9FA] p-6 sticky top-24">
                    <h2 className="text-lg font-['Outfit'] font-bold text-[#0F172A] mb-6">
                      Order Summary
                    </h2>
                    
                    <div className="space-y-4 mb-6">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Move Date</span>
                        <span className="font-medium">{quote.move_date}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">From</span>
                        <span className="font-medium">{quote.from_postcode}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">To</span>
                        <span className="font-medium">{quote.to_postcode}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Property</span>
                        <span className="font-medium">{quote.bedrooms} bed {quote.property_type}</span>
                      </div>
                    </div>
                    
                    <div className="border-t border-slate-200 pt-4 space-y-2">
                      <div className="flex justify-between">
                        <span className="text-slate-600">Estimated Total</span>
                        <span className="font-semibold">£{quote.estimated_price?.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-lg">
                        <span className="font-semibold text-[#0F172A]">Deposit (25%)</span>
                        <span className="font-bold text-[#FF5A1F]">£{booking.deposit_amount?.toFixed(2)}</span>
                      </div>
                    </div>
                    
                    <div className="mt-6 pt-4 border-t border-slate-200">
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <Shield className="w-4 h-4" />
                        <span>Secure payment processing</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Payment Form */}
                <div className="lg:col-span-3">
                  <h1 className="text-3xl font-['Outfit'] font-bold text-[#0F172A] mb-2">
                    Pay Deposit
                  </h1>
                  <p className="text-slate-600 mb-8">
                    Secure your booking with a 25% deposit. The remaining balance is due on moving day.
                  </p>

                  {/* Payment Method Selection */}
                  <div className="mb-8">
                    <label className="label-text mb-4 block">Payment Method</label>
                    <div className="grid grid-cols-2 gap-4">
                      <button
                        onClick={() => setPaymentMethod("stripe")}
                        className={`p-4 border flex items-center justify-center gap-2 transition-all ${
                          paymentMethod === "stripe"
                            ? "border-[#FF5A1F] bg-[#FF5A1F]/5"
                            : "border-slate-200 hover:border-slate-300"
                        }`}
                        data-testid="payment-method-stripe"
                      >
                        <CreditCard className="w-5 h-5" />
                        <span className="font-semibold">Card</span>
                      </button>
                      <button
                        onClick={() => setPaymentMethod("paypal")}
                        className={`p-4 border flex items-center justify-center gap-2 transition-all ${
                          paymentMethod === "paypal"
                            ? "border-[#FF5A1F] bg-[#FF5A1F]/5"
                            : "border-slate-200 hover:border-slate-300"
                        }`}
                        data-testid="payment-method-paypal"
                      >
                        <span className="font-semibold">PayPal</span>
                      </button>
                    </div>
                  </div>

                  {/* Stripe Payment */}
                  {paymentMethod === "stripe" && (
                    <div>
                      <button
                        onClick={handleStripePayment}
                        disabled={processingStripe}
                        className="w-full btn-primary h-14 text-lg flex items-center justify-center gap-2"
                        data-testid="pay-with-stripe-btn"
                      >
                        {processingStripe ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <>
                            <CreditCard className="w-5 h-5" />
                            Pay £{booking.deposit_amount?.toFixed(2)} with Card
                          </>
                        )}
                      </button>
                      <p className="text-center text-sm text-slate-500 mt-4">
                        You'll be redirected to Stripe's secure checkout
                      </p>
                    </div>
                  )}

                  {/* PayPal Payment */}
                  {paymentMethod === "paypal" && (
                    <PayPalScriptProvider options={PAYPAL_OPTIONS}>
                      <PayPalButtons
                        style={{ layout: "vertical", height: PAYPAL_BUTTON_HEIGHT }}
                        createOrder={(data, actions) => {
                          return actions.order.create({
                            purchase_units: [{
                              amount: {
                                value: booking.deposit_amount?.toFixed(2),
                                currency_code: "GBP"
                              },
                              description: `Hitech Transit Deposit - ${booking.booking_id.slice(0, 8).toUpperCase()}`
                            }]
                          });
                        }}
                        onApprove={handlePayPalApprove}
                        onError={() => {
                          toast.error("PayPal payment failed");
                        }}
                      />
                      <p className="text-center text-sm text-slate-500 mt-4">
                        Pay securely with your PayPal account
                      </p>
                    </PayPalScriptProvider>
                  )}

                  {/* Security Notice */}
                  <div className="mt-8 p-4 bg-slate-50 border border-slate-200">
                    <h3 className="font-semibold text-[#0F172A] mb-2 flex items-center gap-2">
                      <Shield className="w-5 h-5 text-[#16A34A]" />
                      Secure Payment
                    </h3>
                    <p className="text-sm text-slate-600">
                      Your payment information is encrypted and secure. We never store your card details.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default PaymentPage;
